import { ref, computed, watch } from 'vue';

import { defineStore } from 'pinia';

import Api from '@/api/Api';

import { getDayJsDuration, dateStrToDayJs } from '@/components/common/dateUtils';


export const useRoversHistoryStore = defineStore('roversHistoryStore', () => {
	const showOnMap = ref(false);
	const autoUpdate = ref(false);

	const { apiData: sessionsApiData, isLoading: isLoadingSessions, doRequest: doRequestSessions } = Api.useActiveRoverSessionsGet();
	const { apiData: statesApiData, isLoading: isLoadingStates, doRequest: doRequestStateHistory, resetData: resetStateHistory } = Api.useRoverStateHistoryGet();

	const selectedRowKeys = ref([]);

	const filterSelectedAddresses = ref([]);
	const filterSelectedUsernames = ref([]);
	const filterSelectedSessionIds = ref([]);

	const sessions = computed(() => sessionsApiData.value.sessions.map(item => ({
		...item,
		key: item.id,
	})));

	const filteredSessions = computed(() => {
		let result = sessions.value;
		if (filterSelectedAddresses.value.length > 0) {
			const set = new Set(filterSelectedAddresses.value);
			result = result.filter(item => {
				const address = item.ip_address;
				return address != null && set.has(address);
			});
		}
		if (filterSelectedUsernames.value.length > 0) {
			const set = new Set(filterSelectedUsernames.value);
			result = result.filter(item => {
				const username = item.username;
				return username != null && set.has(username);
			});
		}
		if (filterSelectedSessionIds.value.length > 0) {
			const set = new Set(filterSelectedSessionIds.value);
			result = result.filter(item => {
				const sessionId = item.session_id;
				return sessionId != null && set.has(sessionId);
			});
		}
		return result;
	});

	const setSelectedRowKeys = keys => {
		selectedRowKeys.value = keys;
	};

	const setFilterState = ({ addresses, usernames, sessionIds }) => {
		if (addresses !== undefined) {
			filterSelectedAddresses.value = addresses;
		}
		if (usernames !== undefined) {
			filterSelectedUsernames.value = usernames;
		}
		if (sessionIds !== undefined) {
			filterSelectedSessionIds.value = sessionIds;
		}
	};

	const onFilterChange = () => {
		const idSet = new Set(sessions.value.map(session => session.id));
		selectedRowKeys.value = selectedRowKeys.value.filter(id => idSet.has(id));
	};

	const historySessions = computed(() => statesApiData.value.sessions);
	const historyStates = computed(() => statesApiData.value.sessions.flatMap(session => session.states));

	watch(historySessions, () => {
		onFilterChange();
	});

	const currentRoversSelected = computed(() => historySessions.value.filter(session => session.rover != null).map(session => ({
		...session.rover,
		session_id: session.session_id,
		username: session.username,
		ip_address: session.ip_address,
	})));

	const roverPaths = computed(() => {
		const result = [];
		for (const session of historySessions.value) {
			const path = session.states;
			const pathResult = [];
			for (let i = 0; i < path.length; i++) {
				const item = path[i];
				if (i < path.length - 1) {
					const nextItem = path[i + 1];
					if (item.b_coord != nextItem.b_coord || item.l_coord != nextItem.l_coord) {
						const isBreak = getDayJsDuration(
							dateStrToDayJs(item.current_datetime),
							dateStrToDayJs(nextItem.current_datetime)
						).asSeconds() > 60;
						pathResult.push({
							...item,
							b_coord2: isBreak ? item.b_coord_end : nextItem.b_coord,
							l_coord2: isBreak ? item.l_coord_end : nextItem.l_coord,
							is_break: isBreak,
							session_id: session.session_id,
							username: session.username,
							ip_address: session.ip_address,
							end_datetime: isBreak ? item.end_datetime : nextItem.current_datetime,
						});
					}
				} else {
					pathResult.push({
						...item,
						b_coord2: item.b_coord_end,
						l_coord2: item.l_coord_end,
						is_break: false,
						session_id: session.session_id,
						username: session.username,
						ip_address: session.ip_address,
						end_datetime: item.end_datetime,
					});
				}
			}
			result.push({
				sessionId: session.id,
				path: pathResult,
			});
		}
		return result;
	});

	return {
		showOnMap,
		autoUpdate,
		sessionsApiData,
		statesApiData,
		isLoadingSessions,
		isLoadingStates,
		doRequestSessions,
		doRequestStateHistory,
		resetStateHistory,
		sessions,
		filteredSessions,
		selectedRowKeys,
		historyStates,
		currentRoversSelected,
		roverPaths,
		filterSelectedAddresses,
		filterSelectedUsernames,
		filterSelectedSessionIds,
		setSelectedRowKeys,
		setFilterState,
		onFilterChange,
	};
});
